<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import * as Icon from "@heroicons/vue/24/solid";
import DOMPurify from "dompurify";
import { marked } from "marked";
import dayjs from "dayjs";
import SimpleMDE from "simplemde";
import "simplemde/dist/simplemde.min.css";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { buildPolicyDiff } from "@/utils/policyDiff";

const route = useRoute();
const router = useRouter();
const isNew = computed(() => route.name === "policy-new");
const policyId = ref(isNew.value ? null : Number(route.params.id));
const loading = ref(true);
const policyTree = ref([]);
const document = ref(null);
const versions = ref([]);
const links = ref([]);
const viewMode = ref(isNew.value ? "edit" : "view");
const textareaRef = ref(null);
const fileInput = ref(null);
let editor = null;
let editorIsSyncing = false;

const form = reactive({
  name: "",
  parentId: "",
  expiresAt: "",
});
const content = ref("");
const baseline = reactive({
  name: "",
  parentId: "",
  expiresAt: "",
  content: "",
});
const toast = reactive({ visible: false, tone: "success", message: "" });
let toastTimer = null;

const publishModal = reactive({
  open: false,
  reason: "",
  saving: false,
  suggesting: false,
  suggestionMessage: "",
  suggestionTone: "success",
  suggestionConfidence: "",
  error: "",
});
const importState = reactive({ uploading: false, error: "" });
const reviewState = reactive({
  open: false,
  loading: false,
  mode: "",
  title: "",
  summary: "",
  warnings: [],
  findings: [],
  proposedMarkdown: "",
  beforeMarkdown: "",
  model: "",
  error: "",
  canApply: false,
});
const linkState = reactive({ selectedId: "", saving: false, error: "" });
const skipNavigationWarning = ref(false);
let reasonSuggestionSequence = 0;

const isPolicy = (item) => item?.kind === "policy";
const flattenTree = (nodes, depth = 0, result = []) => {
  for (const node of nodes || []) {
    result.push({ ...node, depth });
    flattenTree(node.children, depth + 1, result);
  }
  return result;
};
const flatTree = computed(() => flattenTree(policyTree.value));
const folderOptions = computed(() =>
  flatTree.value.filter((item) => !isPolicy(item)),
);
const linkedPolicyIds = computed(
  () => new Set(links.value.map((link) => Number(link.related_policy.id))),
);
const policyOptions = computed(() =>
  flatTree.value.filter(
    (item) =>
      isPolicy(item) &&
      Number(item.id) !== Number(policyId.value) &&
      !linkedPolicyIds.value.has(Number(item.id)),
  ),
);

const normalizedForm = () => ({
  name: form.name.trim(),
  parentId: form.parentId ? String(form.parentId) : "",
  expiresAt: form.expiresAt || "",
  content: content.value,
});
const hasUnpublishedChanges = computed(() => {
  const current = normalizedForm();
  return (
    current.name !== baseline.name ||
    current.parentId !== baseline.parentId ||
    current.expiresAt !== baseline.expiresAt ||
    current.content !== baseline.content
  );
});
const publicationStatus = computed(() =>
  document.value?.current_version_id ? "Published" : "Not published",
);
const renderedContent = computed(() =>
  DOMPurify.sanitize(marked.parse(content.value || "")),
);
const diffRows = computed(() =>
  buildPolicyDiff(reviewState.beforeMarkdown, reviewState.proposedMarkdown),
);
const changeCounts = computed(() =>
  diffRows.value.reduce(
    (counts, row) => {
      if (row.type === "added") counts.added += 1;
      if (row.type === "removed") counts.removed += 1;
      return counts;
    },
    { added: 0, removed: 0 },
  ),
);

const { setBreadcrumb } = useBreadcrumbs();
setBreadcrumb("Policy");

const showToast = (message, tone = "success") => {
  toast.message = message;
  toast.tone = tone;
  toast.visible = true;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.visible = false;
  }, 4000);
};

const formatDateTime = (value) =>
  value ? dayjs(value).format("D MMM YYYY, HH:mm") : "Unknown date";

const setEditorValue = (value) => {
  content.value = value || "";
  if (!editor || editor.value() === content.value) return;
  editorIsSyncing = true;
  editor.value(content.value);
  editorIsSyncing = false;
};

const initialiseEditor = async () => {
  await nextTick();
  if (editor || !textareaRef.value) return;
  editor = new SimpleMDE({
    element: textareaRef.value,
    autofocus: isNew.value,
    forceSync: true,
    spellChecker: true,
    status: ["lines", "words"],
    toolbar: [
      "bold",
      "italic",
      "heading",
      "|",
      "quote",
      "unordered-list",
      "ordered-list",
      "|",
      "link",
      "table",
      "horizontal-rule",
      "|",
      "preview",
      "side-by-side",
      "fullscreen",
      "|",
      "guide",
    ],
  });
  editor.value(content.value);
  editor.codemirror.on("change", () => {
    if (!editorIsSyncing) content.value = editor.value();
  });
};

const loadTree = async () => {
  const response = await apiClient.get("/api/policy-documents");
  policyTree.value = response.data || [];
};

const setBaseline = () => {
  const current = normalizedForm();
  Object.assign(baseline, current);
};

const loadPolicy = async () => {
  if (!policyId.value) return;
  const response = await apiClient.get(
    `/api/policy-documents/${policyId.value}`,
  );
  document.value = response.data;
  form.name = response.data.name || "";
  setBreadcrumb(form.name || "Policy");
  form.parentId = response.data.parent_id
    ? String(response.data.parent_id)
    : "";
  form.expiresAt = response.data.expires_at
    ? dayjs(response.data.expires_at).format("YYYY-MM-DD")
    : "";
  setEditorValue(
    response.data.editing_version?.content_md ||
      response.data.current_version?.content_md ||
      "",
  );
  setBaseline();
};

const loadVersions = async () => {
  if (!policyId.value) {
    versions.value = [];
    return;
  }
  const response = await apiClient.get(
    `/api/policy-documents/${policyId.value}/versions`,
  );
  versions.value = response.data || [];
};

const loadLinks = async () => {
  if (!policyId.value) {
    links.value = [];
    return;
  }
  const response = await apiClient.get(
    `/api/policy-documents/${policyId.value}/links`,
  );
  links.value = response.data || [];
};

const loadAll = async () => {
  loading.value = true;
  try {
    await loadTree();
    if (policyId.value) {
      await loadPolicy();
      await Promise.all([loadVersions(), loadLinks()]);
    } else {
      form.parentId = route.query.parent_id
        ? String(route.query.parent_id)
        : "";
      setBaseline();
    }
  } catch (error) {
    showToast(
      error?.response?.data?.message || "Unable to load this policy.",
      "error",
    );
  } finally {
    loading.value = false;
    await initialiseEditor();
  }
};

const openPublish = () => {
  publishModal.error = "";
  publishModal.suggestionMessage = "";
  publishModal.suggestionConfidence = "";
  if (!form.name.trim()) {
    publishModal.error = "Enter a policy name before publishing.";
  } else if (!content.value.trim()) {
    publishModal.error = "Enter policy content before publishing.";
  }
  publishModal.open = true;

  if (publishModal.error || publishModal.reason.trim()) return;

  if (document.value?.current_version_id) {
    suggestChangeReason();
  } else {
    publishModal.reason = "Initial policy publication";
    publishModal.suggestionTone = "success";
    publishModal.suggestionMessage =
      "A default reason was added for the first publication.";
  }
};

const validationMessage = (error, fallback) => {
  const errors = error?.response?.data?.errors;
  const first = errors ? Object.values(errors).flat()[0] : null;
  return (
    first ||
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    fallback
  );
};

const suggestChangeReason = async (replaceExisting = false) => {
  if (!policyId.value || publishModal.suggesting) return;

  const requestSequence = ++reasonSuggestionSequence;
  const reasonAtStart = publishModal.reason;
  publishModal.suggesting = true;
  publishModal.suggestionMessage = "";
  publishModal.suggestionConfidence = "";

  try {
    const response = await apiClient.post(
      `/api/policy-documents/${policyId.value}/suggest-change-reason`,
      {
        name: form.name.trim(),
        parent_id: form.parentId ? Number(form.parentId) : null,
        expires_at: form.expiresAt || null,
        content_md: content.value,
      },
    );

    if (requestSequence !== reasonSuggestionSequence) return;

    if (
      replaceExisting ||
      publishModal.reason === reasonAtStart ||
      !publishModal.reason.trim()
    ) {
      publishModal.reason = response.data.change_reason || "";
      publishModal.error = "";
      publishModal.suggestionConfidence = response.data.confidence || "";
      publishModal.suggestionTone = "success";
      publishModal.suggestionMessage =
        "AI compared the published and proposed policy. Review or edit its suggestion before publishing.";
    } else {
      publishModal.suggestionTone = "warning";
      publishModal.suggestionMessage =
        "Your reason was kept because you started editing while AI was reviewing the changes.";
    }
  } catch {
    if (requestSequence !== reasonSuggestionSequence) return;
    publishModal.suggestionTone = "warning";
    publishModal.suggestionMessage =
      "AI could not suggest a reason. Enter a brief reason manually.";
  } finally {
    if (requestSequence === reasonSuggestionSequence) {
      publishModal.suggesting = false;
    }
  }
};

const closePublish = () => {
  reasonSuggestionSequence += 1;
  publishModal.suggesting = false;
  publishModal.open = false;
};

const publish = async () => {
  publishModal.error = "";
  if (!form.name.trim() || !content.value.trim()) {
    publishModal.error = "A name and policy content are required.";
    return;
  }
  if (publishModal.reason.trim().length < 3) {
    publishModal.error = "Provide a short reason for this publication.";
    return;
  }

  publishModal.saving = true;
  try {
    const payload = {
      name: form.name.trim(),
      parent_id: form.parentId ? Number(form.parentId) : null,
      expires_at: form.expiresAt || null,
      content_md: content.value,
      change_reason: publishModal.reason.trim(),
    };
    const endpoint = policyId.value
      ? `/api/policy-documents/${policyId.value}/publish`
      : "/api/policy-documents/publish";
    const response = await apiClient.post(endpoint, payload);

    if (!policyId.value) {
      policyId.value = Number(response.data.policy_document_id);
      setBaseline();
      skipNavigationWarning.value = true;
      await router.replace({
        name: "policy-editor",
        params: { id: policyId.value },
      });
      skipNavigationWarning.value = false;
    }
    publishModal.open = false;
    publishModal.reason = "";
    publishModal.suggestionMessage = "";
    publishModal.suggestionConfidence = "";
    await loadAll();
    viewMode.value = "view";
    showToast("Policy published and the change reason was logged.");
  } catch (error) {
    publishModal.error = validationMessage(
      error,
      "Unable to publish this policy.",
    );
  } finally {
    publishModal.saving = false;
  }
};

const selectDocx = () => fileInput.value?.click();
const importDocx = async (event) => {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  importState.uploading = true;
  importState.error = "";
  try {
    const payload = new FormData();
    payload.append("document", file);
    const response = await apiClient.post(
      "/api/policy-documents/import-docx",
      payload,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    reviewState.open = true;
    reviewState.mode = "import";
    reviewState.title = "Review DOCX import";
    reviewState.summary =
      "The Word document was converted to Markdown. Review every change before applying it.";
    reviewState.warnings = response.data.warnings || [];
    reviewState.findings = [];
    reviewState.beforeMarkdown = content.value;
    reviewState.proposedMarkdown = response.data.markdown || "";
    reviewState.model = "";
    reviewState.error = "";
    reviewState.canApply = true;
    if (!form.name.trim()) {
      form.name = String(response.data.original_name || "")
        .replace(/\.docx$/i, "")
        .trim();
    }
  } catch (error) {
    importState.error = validationMessage(
      error,
      "Unable to import this DOCX file.",
    );
    showToast(importState.error, "error");
  } finally {
    importState.uploading = false;
  }
};

const runAiReview = async (mode) => {
  if (!form.name.trim() || !content.value.trim()) {
    showToast(
      "Enter a policy name and content before running an AI review.",
      "error",
    );
    return;
  }
  reviewState.open = true;
  reviewState.loading = true;
  reviewState.mode = mode;
  reviewState.title =
    mode === "improve" ? "AI improvement proposal" : "AI policy check";
  reviewState.summary = "";
  reviewState.warnings = [];
  reviewState.findings = [];
  reviewState.beforeMarkdown = content.value;
  reviewState.proposedMarkdown = content.value;
  reviewState.model = "";
  reviewState.error = "";
  reviewState.canApply = false;

  try {
    const endpoint = policyId.value
      ? `/api/policy-documents/${policyId.value}/ai-review`
      : "/api/policy-documents/ai-review";
    const response = await apiClient.post(endpoint, {
      name: form.name.trim(),
      mode,
      content_md: content.value,
    });
    reviewState.summary = response.data.summary || "Review complete.";
    reviewState.findings = response.data.findings || [];
    reviewState.proposedMarkdown =
      response.data.proposed_markdown || content.value;
    reviewState.model = response.data.model || "";
    reviewState.canApply =
      mode === "improve" &&
      reviewState.proposedMarkdown !== reviewState.beforeMarkdown;
  } catch (error) {
    reviewState.error = validationMessage(
      error,
      "The AI review could not be completed.",
    );
  } finally {
    reviewState.loading = false;
  }
};

const compareVersion = (version) => {
  reviewState.open = true;
  reviewState.loading = false;
  reviewState.mode = "history";
  reviewState.title = `Compare version ${version.version_label}`;
  reviewState.summary =
    "Red lines are removed from your current editor content; green lines come from the selected version.";
  reviewState.warnings = [];
  reviewState.findings = [];
  reviewState.beforeMarkdown = content.value;
  reviewState.proposedMarkdown = version.content_md || "";
  reviewState.model = "";
  reviewState.error = "";
  reviewState.canApply = version.content_md !== content.value;
};

const applyProposal = () => {
  setEditorValue(reviewState.proposedMarkdown);
  viewMode.value = "edit";
  reviewState.open = false;
  showToast(
    "Proposal applied to the editor. Publish it when you are satisfied.",
  );
};

const addLink = async () => {
  if (!policyId.value || !linkState.selectedId) return;
  linkState.saving = true;
  linkState.error = "";
  try {
    await apiClient.post(`/api/policy-documents/${policyId.value}/links`, {
      linked_policy_document_id: Number(linkState.selectedId),
    });
    linkState.selectedId = "";
    await loadLinks();
  } catch (error) {
    linkState.error = validationMessage(error, "Unable to link this policy.");
  } finally {
    linkState.saving = false;
  }
};

const removeLink = async (link) => {
  try {
    await apiClient.delete(
      `/api/policy-documents/${policyId.value}/links/${link.id}`,
    );
    await loadLinks();
  } catch (error) {
    showToast(validationMessage(error, "Unable to remove this link."), "error");
  }
};

const deletePolicy = async () => {
  if (!policyId.value || !window.confirm(`Delete "${form.name}"?`)) return;
  try {
    await apiClient.delete(`/api/policy-documents/${policyId.value}`);
    skipNavigationWarning.value = true;
    await router.push({ name: "policies" });
  } catch (error) {
    showToast(
      validationMessage(error, "Unable to delete this policy."),
      "error",
    );
  }
};

const beforeUnload = (event) => {
  if (!hasUnpublishedChanges.value) return;
  event.preventDefault();
  event.returnValue = "";
};

onBeforeRouteLeave(() => {
  if (skipNavigationWarning.value) return true;
  if (
    hasUnpublishedChanges.value &&
    !window.confirm("Leave without publishing your changes?")
  ) {
    return false;
  }
  return true;
});

onMounted(() => {
  window.addEventListener("beforeunload", beforeUnload);
  loadAll();
});
onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", beforeUnload);
  window.clearTimeout(toastTimer);
  editor?.toTextArea();
  editor = null;
});
</script>

<template>
  <main class="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
    <div
      v-if="toast.visible"
      class="fixed right-5 top-5 z-[70] max-w-sm rounded-lg border px-4 py-3 text-sm font-medium shadow-lg"
      :class="
        toast.tone === 'error'
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-emerald-200 bg-white text-emerald-800'
      "
    >
      {{ toast.message }}
    </div>

    <div
      v-if="loading"
      class="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500"
    >
      Loading policy…
    </div>

    <template v-else>
      <header
        class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
      >
        <div>
          <button
            class="mb-2 inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-emerald-700"
            @click="router.push({ name: 'policies' })"
          >
            <Icon.ArrowLeftIcon class="h-4 w-4" />
            Policies
          </button>
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-2xl font-semibold text-slate-900">
              {{ form.name || "New policy" }}
            </h1>
            <span
              class="rounded-full px-2.5 py-1 text-xs font-semibold"
              :class="
                document?.current_version_id
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-slate-100 text-slate-600'
              "
            >
              {{ publicationStatus }}
            </span>
            <span
              v-if="hasUnpublishedChanges"
              class="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800"
            >
              Unpublished changes
            </span>
          </div>
          <p class="mt-1 text-sm text-slate-600">
            Every publication creates an immutable version and records your
            reason.
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <input
            ref="fileInput"
            class="hidden"
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            @change="importDocx"
          />
          <button
            class="action-button"
            :disabled="importState.uploading"
            @click="selectDocx"
          >
            <Icon.ArrowUpTrayIcon class="h-5 w-5" />
            {{ importState.uploading ? "Converting…" : "Import DOCX" }}
          </button>
          <button class="action-button" @click="runAiReview('check')">
            <Icon.MagnifyingGlassCircleIcon class="h-5 w-5 text-violet-600" />
            AI check
          </button>
          <button class="action-button" @click="runAiReview('improve')">
            <Icon.SparklesIcon class="h-5 w-5 text-violet-600" />
            AI improve
          </button>
          <button
            class="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
            @click="openPublish"
          >
            <Icon.ArrowUpCircleIcon class="h-5 w-5" />
            {{
              document?.current_version_id
                ? "Publish changes"
                : "Publish policy"
            }}
          </button>
        </div>
      </header>

      <section class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div class="min-w-0 space-y-4">
          <div
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div class="grid gap-4 md:grid-cols-3">
              <label class="md:col-span-2">
                <span class="text-sm font-medium text-slate-700"
                  >Policy name</span
                >
                <input
                  v-model="form.name"
                  class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="e.g. Infection Prevention Policy"
                />
              </label>
              <label>
                <span class="text-sm font-medium text-slate-700"
                  >Review / expiry date</span
                >
                <input
                  v-model="form.expiresAt"
                  type="date"
                  class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </label>
              <label class="md:col-span-3">
                <span class="text-sm font-medium text-slate-700">Folder</span>
                <select
                  v-model="form.parentId"
                  class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                >
                  <option value="">All policies (root)</option>
                  <option
                    v-for="folder in folderOptions"
                    :key="folder.id"
                    :value="String(folder.id)"
                  >
                    {{ "— ".repeat(folder.depth) }}{{ folder.name }}
                  </option>
                </select>
              </label>
            </div>
          </div>

          <div
            class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div
              class="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3"
            >
              <h2 class="font-semibold text-slate-900">Policy content</h2>
              <div
                class="inline-flex rounded-lg border border-slate-300 bg-white p-1"
              >
                <button
                  class="rounded-md px-3 py-1 text-sm font-medium"
                  :class="
                    viewMode === 'edit'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600'
                  "
                  @click="viewMode = 'edit'"
                >
                  Edit
                </button>
                <button
                  class="rounded-md px-3 py-1 text-sm font-medium"
                  :class="
                    viewMode === 'view'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600'
                  "
                  @click="viewMode = 'view'"
                >
                  Preview
                </button>
              </div>
            </div>
            <div v-show="viewMode === 'edit'" class="policy-editor">
              <textarea
                ref="textareaRef"
                aria-label="Policy Markdown"
              ></textarea>
            </div>
            <article
              v-show="viewMode === 'view'"
              class="policy-markdown min-h-[480px] p-6"
              v-html="renderedContent"
            ></article>
          </div>
        </div>

        <aside class="space-y-6">
          <section
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <h2 class="font-semibold text-slate-900">Related policies</h2>
            <p class="mt-1 text-xs text-slate-500">
              Links work in both directions.
            </p>
            <div v-if="policyId" class="mt-4 space-y-3">
              <div
                v-for="link in links"
                :key="link.id"
                class="flex items-start justify-between gap-2 rounded-lg bg-slate-50 p-3"
              >
                <router-link
                  class="text-sm font-semibold text-emerald-700 hover:underline"
                  :to="{
                    name: 'policy-editor',
                    params: { id: link.related_policy.id },
                  }"
                >
                  {{ link.related_policy.name }}
                </router-link>
                <button
                  class="text-slate-400 hover:text-red-600"
                  title="Remove link"
                  @click="removeLink(link)"
                >
                  <Icon.XMarkIcon class="h-5 w-5" />
                </button>
              </div>
              <p v-if="!links.length" class="text-sm text-slate-500">
                No related policies yet.
              </p>
              <form
                class="space-y-2 border-t border-slate-200 pt-3"
                @submit.prevent="addLink"
              >
                <select
                  v-model="linkState.selectedId"
                  class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">Choose a policy…</option>
                  <option
                    v-for="policy in policyOptions"
                    :key="policy.id"
                    :value="String(policy.id)"
                  >
                    {{ policy.name }}
                  </option>
                </select>
                <button
                  class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  :disabled="!linkState.selectedId || linkState.saving"
                >
                  {{ linkState.saving ? "Linking…" : "Link policy" }}
                </button>
                <p v-if="linkState.error" class="text-xs text-red-600">
                  {{ linkState.error }}
                </p>
              </form>
            </div>
            <p v-else class="mt-3 text-sm text-slate-500">
              Publish this policy before linking it to another.
            </p>
          </section>

          <section
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-slate-900">Version history</h2>
              <span class="text-xs text-slate-500">{{ versions.length }}</span>
            </div>
            <div v-if="versions.length" class="mt-3 space-y-3">
              <div
                v-for="version in versions"
                :key="version.id"
                class="rounded-lg border border-slate-200 p-3"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-semibold text-slate-800"
                      >Version {{ version.version_label }}</span
                    >
                    <span
                      v-if="version.is_current"
                      class="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800"
                      >Current</span
                    >
                  </div>
                  <button
                    class="text-xs font-semibold text-emerald-700 hover:underline"
                    @click="compareVersion(version)"
                  >
                    Compare
                  </button>
                </div>
                <p class="mt-2 text-sm text-slate-700">
                  {{ version.change_reason || "Legacy version" }}
                </p>
                <p class="mt-1 text-xs text-slate-500">
                  {{ version.creator?.name || "Unknown user" }} ·
                  {{
                    formatDateTime(version.published_at || version.created_at)
                  }}
                </p>
              </div>
            </div>
            <p v-else class="mt-3 text-sm text-slate-500">
              No published versions yet.
            </p>
          </section>

          <button
            v-if="policyId"
            class="w-full rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            @click="deletePolicy"
          >
            Delete policy
          </button>
        </aside>
      </section>
    </template>

    <div
      v-if="publishModal.open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4"
      @click.self="closePublish"
    >
      <form
        class="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl"
        @submit.prevent="publish"
      >
        <div class="flex items-start gap-3">
          <div class="rounded-full bg-emerald-100 p-2">
            <Icon.ArrowUpCircleIcon class="h-6 w-6 text-emerald-700" />
          </div>
          <div>
            <h2 class="text-lg font-semibold text-slate-900">
              {{
                document?.current_version_id
                  ? "Publish policy changes"
                  : "Publish policy"
              }}
            </h2>
            <p class="mt-1 text-sm text-slate-600">
              This immediately becomes the current version. The content, author,
              time and reason are retained in the audit history.
            </p>
          </div>
        </div>
        <div class="mt-5">
          <span class="flex items-center justify-between gap-3">
            <label
              for="policy-change-reason"
              class="text-sm font-medium text-slate-700"
              >Change reason</label
            >
            <button
              v-if="document?.current_version_id"
              type="button"
              class="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-50"
              :disabled="publishModal.suggesting"
              @click="suggestChangeReason(true)"
            >
              <Icon.SparklesIcon class="h-4 w-4" />
              {{
                publishModal.suggesting ? "Reviewing changes…" : "Suggest again"
              }}
            </button>
          </span>
          <textarea
            id="policy-change-reason"
            v-model="publishModal.reason"
            rows="3"
            maxlength="500"
            class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
            placeholder="Briefly explain what changed and why"
          ></textarea>
          <span class="mt-1 block text-right text-xs text-slate-400"
            >{{ publishModal.reason.length }}/500</span
          >
        </div>
        <div
          v-if="publishModal.suggesting"
          class="mt-2 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800"
        >
          <Icon.SparklesIcon class="h-5 w-5 animate-pulse" />
          AI is comparing the published and proposed policy…
        </div>
        <div
          v-else-if="publishModal.suggestionMessage"
          class="mt-2 rounded-lg border px-3 py-2 text-sm"
          :class="
            publishModal.suggestionTone === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-violet-200 bg-violet-50 text-violet-800'
          "
        >
          {{ publishModal.suggestionMessage }}
          <span
            v-if="publishModal.suggestionConfidence"
            class="ml-1 font-semibold"
          >
            {{ publishModal.suggestionConfidence }} confidence.
          </span>
        </div>
        <p
          v-if="publishModal.error"
          class="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {{ publishModal.error }}
        </p>
        <div class="mt-5 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            @click="closePublish"
          >
            Cancel
          </button>
          <button
            class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            :disabled="publishModal.saving"
          >
            {{ publishModal.saving ? "Publishing…" : "Publish now" }}
          </button>
        </div>
      </form>
    </div>

    <div
      v-if="reviewState.open"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-2 sm:p-5"
      @click.self="reviewState.open = false"
    >
      <section
        class="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <header
          class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4"
        >
          <div>
            <h2 class="text-lg font-semibold text-slate-900">
              {{ reviewState.title }}
            </h2>
            <p v-if="reviewState.summary" class="mt-1 text-sm text-slate-600">
              {{ reviewState.summary }}
            </p>
            <p v-if="reviewState.model" class="mt-1 text-xs text-slate-400">
              Generated with {{ reviewState.model }}. Manager review is
              required.
            </p>
          </div>
          <button
            class="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            @click="reviewState.open = false"
          >
            <Icon.XMarkIcon class="h-6 w-6" />
          </button>
        </header>

        <div
          v-if="reviewState.loading"
          class="flex min-h-[360px] flex-col items-center justify-center p-10 text-center"
        >
          <Icon.SparklesIcon class="h-10 w-10 animate-pulse text-violet-600" />
          <p class="mt-3 font-medium text-slate-800">Reviewing the policy…</p>
          <p class="mt-1 text-sm text-slate-500">
            This can take a minute for a long document.
          </p>
        </div>
        <div v-else class="min-h-0 flex-1 overflow-y-auto p-5">
          <div
            v-if="reviewState.error"
            class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            {{ reviewState.error }}
          </div>

          <div
            v-if="reviewState.warnings.length"
            class="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <h3 class="text-sm font-semibold text-amber-900">
              Conversion notes
            </h3>
            <ul class="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
              <li v-for="warning in reviewState.warnings" :key="warning">
                {{ warning }}
              </li>
            </ul>
          </div>

          <div v-if="reviewState.findings.length" class="mb-5 space-y-3">
            <h3 class="font-semibold text-slate-900">
              Findings ({{ reviewState.findings.length }})
            </h3>
            <article
              v-for="finding in reviewState.findings"
              :key="finding.id"
              class="rounded-lg border border-slate-200 p-4"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-semibold uppercase"
                  :class="
                    finding.severity === 'high'
                      ? 'bg-red-100 text-red-800'
                      : finding.severity === 'medium'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                  "
                  >{{ finding.severity }}</span
                >
                <span class="text-xs font-semibold uppercase text-slate-500">{{
                  finding.category
                }}</span>
                <span
                  v-if="finding.requires_human_verification"
                  class="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800"
                  >Human verification</span
                >
              </div>
              <p
                v-if="finding.excerpt"
                class="mt-2 border-l-2 border-slate-300 pl-3 text-sm italic text-slate-600"
              >
                {{ finding.excerpt }}
              </p>
              <p class="mt-2 text-sm font-medium text-slate-800">
                {{ finding.suggestion }}
              </p>
              <p class="mt-1 text-sm text-slate-600">{{ finding.rationale }}</p>
            </article>
          </div>
          <p
            v-else-if="reviewState.mode === 'check' && !reviewState.error"
            class="mb-5 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800"
          >
            No specific issues were returned. This is an editorial aid, not a
            compliance approval.
          </p>

          <div v-if="reviewState.mode !== 'check' && !reviewState.error">
            <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 class="font-semibold text-slate-900">
                Line-by-line change preview
              </h3>
              <div class="flex gap-3 font-mono text-xs">
                <span class="text-green-700"
                  >+{{ changeCounts.added }} added</span
                >
                <span class="text-red-700"
                  >−{{ changeCounts.removed }} removed</span
                >
              </div>
            </div>
            <div
              class="max-h-[52vh] overflow-auto rounded-lg border border-slate-300 bg-white font-mono text-xs"
            >
              <div
                v-for="(row, index) in diffRows"
                :key="`${index}-${row.type}`"
                class="grid min-w-[700px] grid-cols-[48px_48px_28px_minmax(0,1fr)] border-b border-slate-100 last:border-b-0"
                :class="
                  row.type === 'added'
                    ? 'bg-green-50 text-green-950'
                    : row.type === 'removed'
                      ? 'bg-red-50 text-red-950'
                      : 'text-slate-700'
                "
              >
                <span
                  class="select-none border-r border-slate-200 px-2 py-1 text-right text-slate-400"
                  >{{ row.oldLine || "" }}</span
                >
                <span
                  class="select-none border-r border-slate-200 px-2 py-1 text-right text-slate-400"
                  >{{ row.newLine || "" }}</span
                >
                <span
                  class="select-none px-2 py-1 font-bold"
                  :class="
                    row.type === 'added'
                      ? 'text-green-700'
                      : row.type === 'removed'
                        ? 'text-red-700'
                        : 'text-slate-300'
                  "
                  >{{
                    row.type === "added"
                      ? "+"
                      : row.type === "removed"
                        ? "−"
                        : " "
                  }}</span
                >
                <span class="whitespace-pre-wrap break-words px-2 py-1">{{
                  row.text || " "
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <footer
          class="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4"
        >
          <button
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
            @click="reviewState.open = false"
          >
            Close
          </button>
          <button
            v-if="reviewState.canApply && !reviewState.loading"
            class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            @click="applyProposal"
          >
            Apply proposal to editor
          </button>
        </footer>
      </section>
    </div>
  </main>
</template>

<style scoped>
.action-button {
  @apply inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50;
}

.policy-editor :deep(.CodeMirror) {
  min-height: 520px;
  border: 0;
  font-size: 14px;
}

.policy-editor :deep(.editor-toolbar) {
  border: 0;
  border-bottom: 1px solid rgb(226 232 240);
  opacity: 1;
}

.policy-markdown :deep(h1) {
  @apply mb-4 mt-6 text-3xl font-bold text-slate-950;
}
.policy-markdown :deep(h2) {
  @apply mb-3 mt-6 text-2xl font-semibold text-slate-900;
}
.policy-markdown :deep(h3) {
  @apply mb-2 mt-5 text-xl font-semibold text-slate-900;
}
.policy-markdown :deep(p) {
  @apply my-3 leading-7 text-slate-700;
}
.policy-markdown :deep(ul) {
  @apply my-3 list-disc space-y-1 pl-6 text-slate-700;
}
.policy-markdown :deep(ol) {
  @apply my-3 list-decimal space-y-1 pl-6 text-slate-700;
}
.policy-markdown :deep(a) {
  @apply text-emerald-700 underline;
}
.policy-markdown :deep(blockquote) {
  @apply my-4 border-l-4 border-emerald-300 bg-emerald-50 px-4 py-2 text-slate-700;
}
.policy-markdown :deep(table) {
  @apply my-4 w-full border-collapse text-sm;
}
.policy-markdown :deep(th) {
  @apply border border-slate-300 bg-slate-100 px-3 py-2 text-left font-semibold;
}
.policy-markdown :deep(td) {
  @apply border border-slate-300 px-3 py-2;
}
.policy-markdown :deep(code) {
  @apply rounded bg-slate-100 px-1 py-0.5 text-sm;
}
</style>
